import { useState, useCallback } from 'react';
import OfficeLayout from '../../components/OfficeLayout';
import { getUsers, formatApiError } from '../../utils/api';
import api from '../../utils/api';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { Plus, Trash2, User, Briefcase, Truck } from 'lucide-react';

export default function Staff() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState('driver');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await getUsers();
      setUsers(data.filter(u => u.role === 'office_staff' || u.role === 'driver' || u.role === 'admin'));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const { refresh } = useAutoRefresh(loadUsers, 30000);

  const admins = users.filter(u => u.role === 'admin');
  const officeStaff = users.filter(u => u.role === 'office_staff');
  const drivers = users.filter(u => u.role === 'driver');

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError(''); setMsg(''); setCreating(true);
    try {
      await api.post('/users/create-simple', { name: name.trim(), role });
      setMsg(`${name.trim()} added as ${role === 'office_staff' ? 'Office Staff' : 'Driver'}`);
      setShowForm(false);
      setName('');
      refresh(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from staff?`)) return;
    try {
      await api.delete(`/users/${userId}`);
      setMsg(`${userName} removed`);
      refresh(true);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const PersonRow = ({ person }) => (
    <div className="flex items-center justify-between px-5 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-sm font-semibold text-[#6B7280]">
          {person.name?.charAt(0) || '?'}
        </div>
        <span className="text-sm font-medium text-[#1A1A2E]">{person.name}</span>
      </div>
      <button onClick={() => handleDelete(person.id, person.name)} className="text-[#9CA3AF] hover:text-red-500 transition-colors" data-testid={`delete-staff-${person.id}`}>
        <Trash2 size={16} />
      </button>
    </div>
  );

  return (
    <OfficeLayout>
      <div data-testid="staff-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#1A1A2E]">Staff</h2>
            <p className="text-sm text-[#6B7280] mt-1">Manage office staff and drivers</p>
          </div>
          <button onClick={() => { setShowForm(!showForm); setError(''); setMsg(''); }}
            className="flex items-center gap-2 bg-[#0D9488] hover:bg-[#0F766E] text-white px-5 py-2.5 rounded-lg font-medium text-sm transition-colors"
            data-testid="add-staff-button">
            <Plus size={16} /> Add Staff
          </button>
        </div>

        {msg && <div className="mb-4 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">{msg}</div>}
        {error && <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

        {showForm && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
            <h3 className="text-sm font-semibold text-[#1A1A2E] mb-4">Add New Staff Member</h3>
            <form onSubmit={handleCreate} className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[220px]">
                <label className="block text-sm font-medium text-[#4B5563] mb-1.5">Name & Surname</label>
                <input value={name} onChange={e => setName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                  required placeholder="e.g. John Smith" data-testid="staff-name-input" autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#4B5563] mb-1.5">Role</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setRole('admin')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                      role === 'admin' ? 'border-[#0D9488] bg-teal-50 text-[#0D9488]' : 'border-gray-200 text-[#6B7280] hover:border-gray-300'
                    }`} data-testid="role-admin">
                    <User size={15} /> Admin
                  </button>
                  <button type="button" onClick={() => setRole('office_staff')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                      role === 'office_staff' ? 'border-[#0D9488] bg-teal-50 text-[#0D9488]' : 'border-gray-200 text-[#6B7280] hover:border-gray-300'
                    }`} data-testid="role-office">
                    <Briefcase size={15} /> Office
                  </button>
                  <button type="button" onClick={() => setRole('driver')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                      role === 'driver' ? 'border-[#0D9488] bg-teal-50 text-[#0D9488]' : 'border-gray-200 text-[#6B7280] hover:border-gray-300'
                    }`} data-testid="role-driver">
                    <Truck size={15} /> Driver
                  </button>
                </div>
              </div>
              <button type="submit" disabled={creating}
                className="px-6 py-2.5 bg-[#0D9488] hover:bg-[#0F766E] text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
                data-testid="create-staff-submit">
                {creating ? 'Adding...' : 'Add'}
              </button>
            </form>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Admins */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
                <User size={16} className="text-purple-600" />
                <h3 className="text-sm font-semibold text-[#1A1A2E]">Admins</h3>
                <span className="ml-auto text-xs text-[#9CA3AF] bg-gray-100 px-2 py-0.5 rounded-full">{admins.length}</span>
              </div>
              {admins.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-[#9CA3AF]">No admins added yet</p>
              ) : (
                admins.map(person => <PersonRow key={person.id} person={person} />)
              )}
            </div>

            {/* Office Staff */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
                <Briefcase size={16} className="text-blue-600" />
                <h3 className="text-sm font-semibold text-[#1A1A2E]">Office Staff</h3>
                <span className="ml-auto text-xs text-[#9CA3AF] bg-gray-100 px-2 py-0.5 rounded-full">{officeStaff.length}</span>
              </div>
              {officeStaff.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-[#9CA3AF]">No office staff added yet</p>
              ) : (
                officeStaff.map(person => <PersonRow key={person.id} person={person} />)
              )}
            </div>

            {/* Drivers */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
                <Truck size={16} className="text-amber-600" />
                <h3 className="text-sm font-semibold text-[#1A1A2E]">Drivers</h3>
                <span className="ml-auto text-xs text-[#9CA3AF] bg-gray-100 px-2 py-0.5 rounded-full">{drivers.length}</span>
              </div>
              {drivers.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-[#9CA3AF]">No drivers added yet</p>
              ) : (
                drivers.map(person => <PersonRow key={person.id} person={person} />)
              )}
            </div>
          </div>
        )}
      </div>
    </OfficeLayout>
  );
}
